/* eslint-disable no-restricted-properties --
 * authNative is the LOGIN transport, and login is the one call that cannot go
 * through apiBridge: the bridge's job is to decide "did the backend answer, or
 * fall back to Supabase?", and there is no Supabase login to fall back to —
 * this file IS what replaced it. It also runs before a session or a workspace
 * exists, which is what the bridge's gating is keyed on.
 *
 * Exempted here rather than by widening package.json's override lists, which
 * apiBridgeGuard.test.ts pins deliberately. See the same note in dataClient.ts.
 */
/**
 * authNative.ts — the browser half of PulseHQ's own login (replaces
 * Supabase Auth).
 *
 * When the workspace is cut over (`apiConfigured()`), the login page and
 * AuthContext talk to OUR FastAPI `/auth/*` endpoints instead of Supabase.
 * Our backend signs the JWT; PostgREST (also ours) validates it and fills
 * `request.jwt.claims`, so RLS keeps working unchanged.
 *
 * Storage: the native session lives in localStorage under `pulsehq_native_session`
 * (`{ access_token, refresh_token, user: { id, email } }`). The token is what
 * every data query sends in `Authorization: Bearer …`.
 *
 * The API surface deliberately mirrors what AuthContext already needs, so the
 * cutover is a swap inside one file, not a rewrite of the app.
 */

import { api, apiConfigured, apiOnly, authConfigured, renewSession } from './apiClient';
import { supabase } from './supabase';

export interface NativeUser {
  id: string;
  email: string | null;
  /** Signed in with a temporary password a super admin made (2026-10-29): the app asks for
   *  a new one before anything else (App.tsx ProtectedRoute → SetPasswordScreen). */
  must_change_password?: boolean;
}

export interface NativeSession {
  access_token: string;
  refresh_token: string;
  user: NativeUser;
}

const STORAGE_KEY = 'pulsehq_native_session';

export function getNativeSession(): NativeSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as NativeSession;
    if (!parsed?.access_token || !parsed?.user?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setNativeSession(session: NativeSession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearNativeSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/** The current access token, if any — callers send it as the bearer. */
export function getNativeAccessToken(): string | null {
  return getNativeSession()?.access_token ?? null;
}

export interface NativeAuthResult {
  error: { message: string } | null;
  session?: NativeSession;
}

/**
 * Sign in via our backend. Returns the same `{ error }` shape AuthContext's
 * `signIn` already returns, so the caller's error handling is unchanged.
 */
export async function nativeSignIn(email: string, password: string): Promise<NativeAuthResult> {
  if (!authConfigured()) {
    return { error: { message: 'Backend not configured' } };
  }
  const res = await api.post<{
    access_token: string;
    refresh_token: string;
    user?: { id: string; email?: string | null; must_change_password?: boolean } | null;
  }>('/auth/login', { email, password });

  if (!res.ok) {
    return { error: { message: res.error || 'Invalid email or password' } };
  }

  const session: NativeSession = {
    access_token: res.data.access_token,
    refresh_token: res.data.refresh_token,
    user: {
      id: res.data.user?.id ?? '',
      email: res.data.user?.email ?? email,
      must_change_password: res.data.user?.must_change_password === true,
    },
  };
  setNativeSession(session);
  seedSupabaseSession(session);
  return { error: null, session };
}

/**
 * Seed supabase-js with OUR native JWT so every data query through the
 * self-hosted PostgREST carries it (RLS then filters by `auth.uid()` from the
 * token's `sub`). supabase-js sends the session's access_token as the
 * `Authorization: Bearer` header on PostgREST requests — exactly what our
 * PostgREST validates against AUTH_JWT_SECRET.
 */
export function seedSupabaseSession(session: NativeSession): void {
  if (!apiConfigured()) return;
  void supabase.auth.setSession({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  }).catch(() => {
    /* Non-fatal: auth flows set the session on the next sign-in anyway. */
  });
}

/**
 * Clear the supabase-js session on sign-out so stale tokens aren't reused.
 */
export function clearSupabaseSession(): void {
  if (!apiConfigured()) return;
  void supabase.auth.signOut().catch(() => {
    /* Non-fatal. */
  });
}

/**
 * Exchange a refresh token for a fresh access token. Returns the new pair,
 * or null when it could not be renewed.
 *
 * The exchange itself lives in apiClient's `renewSession()` (2026-10-24), which
 * every 401 there also uses, so an explicit call here joins a renewal that is
 * already in flight instead of starting a second one. A dead refresh token
 * clears the session and signs the person out from there.
 */
export async function nativeRefresh(): Promise<NativeSession | null> {
  const outcome = await renewSession();
  return outcome === 'renewed' ? getNativeSession() : null;
}

export async function nativeSignOut(): Promise<void> {
  clearNativeSession();
}

/** True while the signed-in person still has the temporary password a super admin gave
 *  them (backend/app/api/v1/people.py). */
export function mustChangePassword(): boolean {
  return getNativeSession()?.user?.must_change_password === true;
}

/**
 * Set my own password (POST /auth/password, which also clears the server's mark). On success
 * the mark is cleared in this browser too, so the app opens normally.
 */
export async function changeOwnPassword(current: string, next: string): Promise<{ error: string | null }> {
  const res = await apiOnly.post<{ ok: string }>('/auth/password', {
    current_password: current,
    new_password: next,
  });
  if (!res.ok) {
    const e = (res.error ?? '').trim();
    return { error: e && !e.startsWith('[object') ? e : 'The password could not be changed.' };
  }
  const s = getNativeSession();
  if (s) setNativeSession({ ...s, user: { ...s.user, must_change_password: false } });
  return { error: null };
}

/**
 * True when the current session is a native one (backend-issued). Used by
 * AuthContext to decide whether the profile lookup should trust the user id
 * it carries. A session that exists but isn't native is Supabase's.
 */
export function hasNativeSession(): boolean {
  return apiConfigured() && getNativeSession() !== null;
}
